import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
// import "emoji-mart/css/emoji-mart.css";
import Label from "./Label";
import Input from "./Input";
import HelpText from "./HelpText";
import ColorSwab from "@components/inputs/color-swab-and-picker";
import Button from "@components/ui/Button";
import ClickToCopy from "@components/shared/click-to-copy";
// assets
import "@styles/synchrony-buttons-selectors-theme.scss";
// import "emoji-mart/css/emoji-mart.css";
import Picker from "@emoji-mart/react";
// utilities
import { setInputValue } from "@utils/input-helpers";
import { checkErrorTypes } from "@utils/custom-errors";
import debounce from "lodash/debounce";
// action
import { setToastMessage } from "@actions/global";

const style = {
  toggleStyle:
    "!outline-none !border-none !bg-transparent !text-color-teal !h-6 !text-xs-6",
  colorPickerStyle: {
    mainContainer: "mr-4",
    subContainer: "w-20 h-20",
  },
};

const InputField = ({
  dataTestID,
  labelText,
  customLabelStyle,
  customId,
  className = "",
  inputType = "text",
  inputName,
  inputWidth,
  inputValue = "",
  alternativeValue,
  onChangeCallback = (value) => {},
  onBlurCallback = (value) => {},
  onFocusCallback = () => {},
  onKeyDownCallback = (value) => {},
  debounceActive,
  placeholderText,
  disabled = false,
  inputLimit = 900,
  required,
  messageText,
  errorPresent,
  errorMessageText,
  emojiOption,
  emojiCallback,
  defaultContainerStyle = true,
  containerWidth = "w-80",
  containerHeight = "h-[3.75rem]",
  customContainerStyle = "",
  customHelpTextStyle = "",
  inputFieldContainer = "",
  customInputStyle,
  leftIcon,
  showColorSwab = false,
  colorHasNoHash,
  onColorCloseCallback,
  inputTypeOfErrors,
  inputErrorCallback,
  fillAvailableInput,
  displayCustomError,
  showClickToCopy = false,
  clickToCopyValue = "",
}) => {
  const dispatch = useDispatch();
  const [fieldName, setFieldName] = useState(inputName);
  const [inputErr, setInputErr] = useState(errorPresent);
  const [localErrorCondition, setLocalErrorCondition] = useState("");

  const sendErrors = (
    localInputValue,
    localInputName,
    localInputTypeOfErrors
  ) => {
    let errorMessages = checkErrorTypes(
      localInputValue,
      localInputTypeOfErrors,
      inputLimit
    );
    if (inputErrorCallback) {
      inputErrorCallback(
        localInputName,
        !!(errorMessages && errorMessages.length)
      );
    }
    if (errorMessages && errorMessages.length) {
      setInputErr(true);
      setLocalErrorCondition(
        displayCustomError
          ? `${errorMessages} ${errorMessageText}`
          : errorMessages
      );
    } else if (errorPresent && errorMessageText) {
      setInputErr(true);
      setLocalErrorCondition(errorMessageText);
    } else {
      setInputErr(false);
      setLocalErrorCondition("");
    }
  };

  useEffect(() => {
    function syncErrors() {
      setInputErr(true);
      if (inputTypeOfErrors) {
        sendErrors(inputValue, inputName, inputTypeOfErrors);
      } else {
        setLocalErrorCondition(errorMessageText);
      }
    }
    if (errorPresent) {
      syncErrors();
    }
    return () => {
      setInputErr(false);
      setLocalErrorCondition("");
    };
  }, [errorPresent, errorMessageText, inputErr, localErrorCondition]);

  const [inputFocus, setFocus] = useState(false);
  const [overridenType, setOverriddenType] = useState(null);
  const [value, setValue] = useState(inputValue);
  const [showEmojis, setShowEmoji] = useState(false);
  const handleChangeDebounce = useCallback(
    debounce((value) => onChangeCallback(value), 200),
    []
  );

  const emojiPicker = useRef();
  const nodeToCopy = useRef(null);

  const [valueCount, setValueCount] = useState(0);
  useEffect(() => {
    if (inputValue !== value) {
      setValue(inputValue);
    }
    if (inputValue !== value || (inputValue && !valueCount)) {
      if (!alternativeValue) {
        setValueCount(inputValue ? inputValue.length : 0);
      } else {
        setValueCount(alternativeValue.length);
      }
    }
  }, [inputValue]);

  useEffect(() => {
    function syncValue() {
      setValue(inputValue);
      setFieldName(inputName);
      if (
        !inputTypeOfErrors &&
        inputLimit &&
        ((!alternativeValue && inputValue && inputValue.length > inputLimit) ||
          (alternativeValue && alternativeValue.length > inputLimit))
      ) {
        setInputErr(true);
        setLocalErrorCondition(
          `This field cannot exceed ${inputLimit} characters.`
        );
      }
    }
    if (inputValue !== value || inputName !== fieldName) {
      syncValue();
    }
    return () => {
      setInputErr(false);
    };
  }, [inputValue, inputName]);

  useEffect(() => {
    if (showEmojis) {
      document.addEventListener("click", closeMenu);
    }
    return () => {
      document.removeEventListener("click", closeMenu);
    };
  }, [showEmojis]);

  const addEmoji = (e) => {
    let combinedVal = value + e.native;
    setValue(combinedVal);
    onChangeHandler(combinedVal);
  };

  const renderEmojiPicker = () => {
    return showEmojis ? (
      <span
        className="emoji-selector-container"
        ref={(el) => (emojiPicker.current = el)}
      >
        <Picker
          title="emojiPicker"
          onEmojiSelect={(e) => {
            addEmoji(e);
            emojiCallback ? emojiCallback() : null;
          }}
        />
      </span>
    ) : (
      <p
        className="emoji-selector-button absolute top-[-7] right-0.5"
        onClick={() => setShowEmoji(true)}
      >
        {String.fromCodePoint(0x1f60a)}
      </p>
    );
  };

  const closeMenu = (e) =>
    emojiPicker.current !== null && !emojiPicker.current.contains(e.target)
      ? setShowEmoji(false)
      : undefined;

  const onChangeHandler = (e) => {
    let targetVal = setInputValue(e);
    if (onChangeCallback) {
      if (inputTypeOfErrors) {
        sendErrors(targetVal, inputName, inputTypeOfErrors);
      }
      onChangeCallback(targetVal);
    } else {
      if (debounceActive) {
        handleChangeDebounce(targetVal);
      }
      setValue(targetVal);
    }
  };

  const onBlurHandler = (e) => {
    setFocus(false);
    if (onBlurCallback) {
      onBlurCallback(e);
    }
  };

  const onFocusHandler = (e) => {
    setFocus(true);
    onFocusCallback ? onFocusCallback(e) : null;
  };

  const inputFieldId = `input-field-${customId || inputName}`;
  const showPasswordToggle = inputType === "password";
  const inputHighlight = (condition) =>
    condition
      ? condition === errorPresent
        ? "shadow-[0px_0px_1px_1px_#c63527]"
        : "shadow-[0px_0px_1px_1px_#34657f]"
      : ""; //{ border: `2px solid ${condition === errorPresent ? colors.brick : colors.teal}` } : {}

  return (
    <div
      className={`${
        containerWidth || "w-max"
      } ${containerHeight} ${customContainerStyle} ${
        className ? ` ${className}` : ""
      } `}
      aria-describedby={`${inputName}${errorPresent ? `error` : "helper-text"}`}
    >
      <div
        className={`input-field-container ${inputFieldContainer} ${
          defaultContainerStyle
            ? `${inputHighlight(
                errorPresent || inputFocus
              )} default-input-container-style`
            : ""
        } ${disabled ? "disabled" : ""}`.trim()}
      >
        {leftIcon}
        <div className="input-label-field-wrapper">
          <div
            className={`input-label-field-container ${
              disabled ? "disabled" : ""
            } ${
              labelText ? "h-2/6 justify-between" : "h-0 justify-end"
            } overflow-hidden`.trim()}
          >
            {labelText && (
              <Label
                labelText={labelText}
                formInputType="input"
                formInputName={inputName}
                customLabelStyle={`mt-1 mx-2 mb-0 ${customLabelStyle}`}
              />
            )}
            {emojiOption && renderEmojiPicker()}
          </div>
          <div
            className={`input-label-field-container ${
              disabled ? "disabled" : ""
            } ${labelText ? "h-2/3" : "h-full"}`.trim()}
          >
            <Input
              dataTestID={dataTestID}
              onChangeCallback={(e) => onChangeHandler(e)}
              onBlurCallback={(e) => onBlurHandler(e)}
              inputName={inputName}
              required={required}
              inputValue={value}
              customInputStyle={customInputStyle}
              placeholderText={placeholderText}
              disabled={disabled}
              onFocusCallback={onFocusHandler}
              customId={customId}
              inputType={overridenType || inputType}
              externalContainer
              returnErrorStatus={setInputErr}
              inputWidth={inputWidth}
              inputContainerStyle={
                showPasswordToggle || showColorSwab ? "flex-[1]" : ""
              }
              fillAvailableInput={fillAvailableInput}
              onKeyDownCallback={(e) => {
                if (onKeyDownCallback) {
                  onKeyDownCallback(e);
                }
              }}
            />
            {showPasswordToggle && (
              <Button
                theme="primary"
                customStyle={style.toggleStyle}
                ariaLabel={!overridenType ? "Show" : "Hide"}
                componentID={inputFieldId}
                label={!overridenType ? "Show" : "Hide"}
                customGATag={inputFieldId}
                onClickCallback={() =>
                  setOverriddenType(!overridenType ? "text" : null)
                }
              />
            )}
            {showColorSwab && (
              <ColorSwab
                returnErrorStatus={setInputErr}
                colorPickerId={customId}
                color={colorHasNoHash ? "#" + value : value}
                colorPickerStyle={{ ...style.colorPickerStyle }}
                colorPickerEnabled={!disabled}
                colorPickerCallback={(color) => {
                  onChangeHandler({
                    target: {
                      name: inputName,
                      value: colorHasNoHash ? color.slice(1) : color,
                    },
                  });
                }}
                onColorCloseCallback={onColorCloseCallback}
                colorSwabStyle="w-8 h-8"
              />
            )}
            {showClickToCopy && (
              <div>
                {nodeToCopy && (
                  <ClickToCopy
                    nodeToCopy={nodeToCopy.current}
                    notificationCallback={() =>
                      dispatch(setToastMessage(`${labelText} has been copied.`))
                    }
                  />
                )}
                {clickToCopyValue && (
                  <span
                    ref={nodeToCopy}
                    className={`${
                      disabled
                        ? "text-color-disabled-input"
                        : "text-color-white"
                    }`}
                  >
                    {clickToCopyValue}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="subtext-field-container flex mt-1">
        {inputLimit && (
          <HelpText
            customHelpTextStyle="mr-2"
            id={`${inputName}-limit`}
            text={`${valueCount}/${inputLimit}`}
            error={Boolean(inputErr)}
          />
        )}
        {(messageText || (inputErr && localErrorCondition)) && (
          <HelpText
            id={inputName}
            text={inputErr ? localErrorCondition : messageText}
            error={Boolean(inputErr)}
            customHelpTextStyle={customHelpTextStyle}
          />
        )}
      </div>
    </div>
  );
};

InputField.propTypes = {
  /** label text for input */
  labelText: PropTypes.string,
  /** optional string used to add additional style to label field */
  customLabelStyle: PropTypes.string,
  /** sets custom id overrides inputName `input__${customId} */
  customId: PropTypes.string,
  /** custom className*/
  className: PropTypes.string,
  /** sets name to input field  determines input id `input__${inputName}` and className tag `${inputName}-input-field*/
  inputType: PropTypes.string,
  /** determines type of input */
  inputName: PropTypes.string.isRequired,
  /** default inputValue */
  inputValue: PropTypes.string,
  /** optional function triggered when input changes */
  onChangeCallback: PropTypes.func,
  /** flag for triggering debounce onChangeCallback */
  debounceActive: PropTypes.bool,
  /** optional function triggered when user tabs out or clicks outside of field */
  onBlurCallback: PropTypes.func,
  /** optional function triggered when input is focused */
  onFocusCallback: PropTypes.func,
  /** placeholder text, "Disabled" if disabled flag is true and disabledValue is null */
  placeholderText: PropTypes.string,
  /** indicates if input is required */
  required: PropTypes.bool,
  /** disables input if true, changes placeholderText to "Disabled" if disabledValue is null */
  disabled: PropTypes.bool,
  /** the text below the input field */
  messageText: PropTypes.string,
  /** bool to highlight error text in red */
  errorPresent: PropTypes.bool,
  /** error message for invalid input */
  errorMessageText: PropTypes.string,
  /** boolean option to use emojis */
  emojiOption: PropTypes.bool,
  /** boolean option to use default border wrapping container style */
  defaultContainerStyle: PropTypes.bool,
  /** custom input width set as "##rem" or other css size */
  containerWidth: PropTypes.string,
  /** custom input height set as "##rem" or other css size */
  containerHeight: PropTypes.string,
  /** optional object used to add additional style to input field */
  customContainerStyle: PropTypes.string,
  /** optional object used to add icon to left of input field */
  leftIcon: PropTypes.node,
  /** boolean option to use colorpicker */
  showColorSwab: PropTypes.bool,
  /** boolean option to use set input values without hash */
  colorHasNoHash: PropTypes.bool,
  /** optional function triggered when color picker closes */
  onColorCloseCallback: PropTypes.func,
  /** array of input validators to check for at local level */
  inputTypeOfErrors: PropTypes.array,
  /** int to determine smallest length of chars for input */
  inputLowerBoundLimit: PropTypes.number,
  /** function to send local errors to parent component */
  inputErrorCallback: PropTypes.func,
  /** adds class vendor prefixes for input filling entire width */
  fillAvailableInput: PropTypes.bool,
};

export default InputField;
